import { useEffect, useMemo, useState, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import { getGroupById } from "../../../../store/actions/group";

import LinkUi from "../../../../components/Link";
import Snackbar from "../../../../components/Snackbar";

import {
  getPosts,
  resetGetPosts,
  resetGetScheduledPosts,
  resetGetFeaturedPosts,
} from "../../../../store/actions/post";
import CommunityTabs from "../../Community/components/Tabs";
import { resetNewCategory, resetUpdateCategory } from "../../../../store/actions/category";
import { useLayout } from "../../../../providers/LayoutProvider";
import Grid from "../../../../components/Grid";
import CallToAction from "../../components/CallToAction";
import ModalShareLink from "../../components/ModalShareLink";

import ArticlesList from "./components/ArticlesList";
import postIcon from "../../../../assets/icons/post-icon.svg";
import categoryIcon from "../../../../assets/icons/category-icon.svg";
import targetIcon from "../../../../assets/icons/target.svg";
import sortIcon from "../../../../assets/icons/sort-icon.svg";
import { CategoryDto } from "../../../../types/category";
import { Post } from "../../../../types/post";
import FloatingMenu from "./components/FloatingMenu";
import { useWindowSize } from "../../../../hooks/useWindowSize";
import Icon from "../../../../components/Icon";
import ModalConfirmDelete from "../components/DeleteLeadMagnetModal";

const Component = ({
  group,
  groupStates,
  posts,
  postsStates,
  featuredPosts,
  featuredPostsStates,
  scheduledPostsStates,
  allCategories,
  match,
  setFeaturedPostStates,
  isLoggedIn,
  newCategory,
  newCategoryStates,
  updatedCategoryStates,
}) => {
  const { isMobile } = useWindowSize()
  const dispatch = useDispatch();
  const { layout, setLayout } = useLayout();
  const [leadMagnetToDelete, setLeadMagnetToDelete] = useState<Post>();
  const [categoryManagerVisible, setCategoryManagerVisible] = useState<boolean>(false);
  const [createNewCategorySidebarVisible, setCreateNewCategorySidebarVisible] = useState<boolean>(false);
  const [categoryToEdit, setCategoryToEdit] = useState<CategoryDto>();
  const [showShareSnackbar, setShowShareSnackbar] = useState<boolean>(false);

  const handleShare = useCallback(() => {
    setShowShareSnackbar(true);
  }, []);

  const isRestrictedGroup = useMemo(() => {
    return !!group && group.group_type !== "community" && group.group_type !== "free_experience";
  }, [group]);

  const initPage = useCallback(() => {
    if (!group) return;

    dispatch(getPosts({
      pagination: {
        paginate: false
      },
      filters: {
        group_id: group.id
      }
    }));
  }, [group, dispatch]);

  useEffect(() => {
    const idGroup = Number(match?.params?.idGroup);
    const needsFetch = !group || group?.id !== idGroup;
    if (idGroup && needsFetch && !groupStates.loading) {
      dispatch(getGroupById({ group_id: idGroup }));
    }
  }, [match?.params?.idGroup, group, groupStates.loading, dispatch]);

  useEffect(() => {
    if (group) initPage();
  }, [group]);

  useEffect(() => {
    return () => {
      dispatch(resetGetPosts());
      dispatch(resetGetScheduledPosts());
      dispatch(resetGetFeaturedPosts());
    };
  }, [isLoggedIn]);

  useEffect(() => {
    setLayout({
      ...layout,
      page: {
        title: "Tu Audiencia",
        description: "Listado de lead magnets",
      },
      dashboard: {
        tabs: {
          visible: !!group,
          activeTab: "home",
          component: [<CommunityTabs active="library" />],
        },
      },
      header: {
        visible: !!group,
        actions: !isMobile ? [
          < LinkUi
            options={{
              type: "filled",
              size: "lg",
              skin: "purple",
              native: true,
            }}
            href={`/dashboard/community/${group?.id}/lead-magnets/new`}
            style={{ minWidth: "210px", gap: 4 }}
          >
            <Icon icon={targetIcon} size="24px" color="white" />
            Crear contenido gratuíto
          </LinkUi >,
        ] : [],
      },
      show_whatsapp: false,
    });
  }, [group, isMobile]);

  useEffect(() => {
    if (newCategoryStates.success) {
      setCreateNewCategorySidebarVisible(false)

      setTimeout(() => {
        dispatch(resetNewCategory());
      }, 3500);
    }

    if (newCategoryStates.error) {
      setTimeout(() => {
        dispatch(resetNewCategory());
      }, 3500);
    }
  }, [newCategoryStates]);

  useEffect(() => {
    if (updatedCategoryStates.success) {
      setCategoryToEdit(undefined)

      setTimeout(() => {
        dispatch(resetUpdateCategory());
      }, 3500);
    }

    if (updatedCategoryStates.error) {
      setTimeout(() => {
        dispatch(resetUpdateCategory());
      }, 3500);
    }
  }, [updatedCategoryStates]);

  return (
    <>
      {!isMobile && isRestrictedGroup && (
        <>
          <Grid.Container>
            <Grid.Row>
              <Grid.Col md={4}>
                <CallToAction
                  title="Subir nuevo artículo"
                  link={`/dashboard/${group?.group_type}/${group?.id}/posts/new`}
                  options={{ size: "small", preffix: postIcon }}
                />
              </Grid.Col>
              <Grid.Col md={4}>
                <CallToAction
                  title="Crear nueva categoría"
                  action={() => setCreateNewCategorySidebarVisible(true)}
                  options={{ size: "small", preffix: categoryIcon }}
                />
              </Grid.Col>
              <Grid.Col md={4}>
                <CallToAction
                  title="Administrar categorías"
                  action={() => setCategoryManagerVisible(true)}
                  options={{ size: "small", preffix: sortIcon }}
                />
              </Grid.Col>
            </Grid.Row>
          </Grid.Container>

          <div style={{ height: 24 }} />
        </>
      )}

      <ArticlesList
        setLeadMagnetToDelete={setLeadMagnetToDelete}
        onShare={handleShare}
      />

      <ModalShareLink />

      <ModalConfirmDelete post={leadMagnetToDelete} />


      {isMobile && (
        <FloatingMenu
          setCategoryManagerVisible={setCategoryManagerVisible}
          setCreateNewCategorySidebarVisible={setCreateNewCategorySidebarVisible}
        />
      )}

      <Snackbar
        visible={showShareSnackbar}
        options={{
          type: "success",
          position: "right",
          time: 3000,
          animation: "bounceInUp",
        }}
        onHide={() => setShowShareSnackbar(false)}
      >
        ¡Link copiado al portapapeles!
      </Snackbar>
    </>
  );
};

const state = ({ groupStore, postStore, categoryStore, userStore }) => {
  const { data: newCategory, states: newCategoryStates } = categoryStore.newCategory;
  const { data: updatedCategory, states: updatedCategoryStates } = categoryStore.updateCategory;
  const { data: posts, states: postsStates } = postStore.listPosts;

  return {
    group: groupStore.group.data,
    groupStates: groupStore.group.states,
    posts,
    postsStates,
    featuredPosts: postStore.featuredPosts.data,
    featuredPostsStates: postStore.featuredPosts.states,
    scheduledPostsStates: postStore.scheduledPosts.states,
    setFeaturedPostStates: postStore.setFeaturedPost.states,
    allCategories: categoryStore.allCategories.data,
    isLoggedIn: userStore.isLoggedIn,
    newCategory,
    newCategoryStates,
    updatedCategory,
    updatedCategoryStates
  };
};

export default connect(state)(Component);
